import { useState, useMemo } from 'react';
import { Container, Typography, Box, Tab, Tabs, CircularProgress, Alert, Skeleton, Stack } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import DefaultLayout from '../layouts/DefaultLayout';
import GlobalHeader from '../components/GlobalHeader';
import { usePermissions } from '../hooks/usePermissions';
import PermissionDenied from '../components/PermissionDenied';
import { queryActivePackingOperationsList, queryCompletedPackingOperationsList, PackingOperationList } from '../schema';
import { computeTripIssuesTabCount } from '../utils/tripIssuesCount';
import CreateOperationDialog from '../components/CreateOperationDialog';
import CreateTripDialog from '../components/CreateTripDialog';
import DeleteTripDialog from '../components/DeleteTripDialog';
import OperationsListSection from '../components/OperationsListSection';
import TabPanel from '../components/TabPanel';
import TripsListSection from '../components/TripsListSection';
import { formatOperationSummary, getOperationProgress } from '../utils/operationDisplay';
import type { OperationSummaryInput } from '../utils/operationDisplay';
import { db } from '../instant';

const TAB_OPERATIONS = 0;
const TAB_TRIPS = 1;

export default function LogisticsScreen() {
    const { canViewInventory, isLoading: permissionsLoading } = usePermissions();
    const location = useLocation();
    const navigate = useNavigate();
    const pathname = location.pathname;
    const activeTab = pathname.endsWith('/trips') ? TAB_TRIPS : TAB_OPERATIONS;
    const [createOperationDialogOpen, setCreateOperationDialogOpen] = useState(false);
    const [createTripDialogOpen, setCreateTripDialogOpen] = useState(false);
    const [deleteTripDialogOpen, setDeleteTripDialogOpen] = useState(false);
    const [tripToDelete, setTripToDelete] = useState<{ id: string; name?: string | null } | null>(null);
    const [deleteTripError, setDeleteTripError] = useState<string | null>(null);
    const [tripSearchQuery, setTripSearchQuery] = useState('');
    const [expandedOperationsSections, setExpandedOperationsSections] = useState<Set<string>>(
        new Set(['activeOperation', 'completedOperations'])
    );

    const handleOperationsAccordionChange = (id: string) => (_e: React.SyntheticEvent, isExpanded: boolean) => {
        setExpandedOperationsSections(prev => {
            const next = new Set(prev);
            if (isExpanded) next.add(id);
            else next.delete(id);
            return next;
        });
    };

    const {
        data: activeOperationsData,
        isLoading: activeOperationsLoading,
        error: activeOperationsError,
    } = queryActivePackingOperationsList();
    const {
        data: completedOperationsData,
        isLoading: completedOperationsLoading,
        error: completedOperationsError,
    } = queryCompletedPackingOperationsList();
    const {
        data: tripsData,
        isLoading: tripsLoading,
        error: tripsError,
    } = db.useQuery(
        activeTab === TAB_TRIPS
            ? {
                  trips: {
                      events: {
                          packingListItems: {
                              itemType: {
                                  category: {},
                              },
                          },
                      },
                      sourceStorageLocation: {},
                      destStorageLocation: {},
                      tripDagNodes: {},
                      tripDagEdges: {},
                      packingOperations: {
                          packingTasks: {},
                          $: { order: { createdAt: 'asc' } },
                      },
                      $: { order: { name: 'asc' } },
                  },
                  trailerCargoIncompatibilities: {
                      trailerItemType: {},
                      cargoItemType: {},
                  },
              }
            : null
    );
    const { data: itemTypesData } = db.useQuery(
        activeTab === TAB_TRIPS
            ? {
                  itemTypes: {
                      category: {},
                      $: { order: { order: 'asc' } },
                  },
              }
            : null
    );
    const { data: itemTypesWithLocationsData } = db.useQuery(
        activeTab === TAB_TRIPS
            ? {
                  itemTypes: {
                      category: {},
                      itemLocations: {
                          event: {},
                          storageLocation: {},
                      },
                      $: { order: { order: 'asc' } },
                  },
              }
            : null
    );

    const activeOperations = useMemo(() => activeOperationsData?.packingOperations || [], [activeOperationsData]);
    const completedOperations = useMemo(() => completedOperationsData?.packingOperations || [], [completedOperationsData]);
    const operationsLoading = activeOperationsLoading || completedOperationsLoading;
    const trips = useMemo(() => tripsData?.trips || [], [tripsData]);

    const tripIssueCounts = useMemo(() => {
        const rows = tripsData?.trailerCargoIncompatibilities ?? [];
        const itemTypes = itemTypesData?.itemTypes ?? [];
        const withLocs = itemTypesWithLocationsData?.itemTypes ?? [];
        const counts: Record<string, number> = {};
        for (const trip of trips) {
            counts[trip.id] = computeTripIssuesTabCount(trip, rows, itemTypes, withLocs);
        }
        return counts;
    }, [trips, tripsData?.trailerCargoIncompatibilities, itemTypesData?.itemTypes, itemTypesWithLocationsData?.itemTypes]);

    const filteredTrips = useMemo(() => {
        return trips
            .filter(trip => !tripSearchQuery.trim() || (trip.name ?? '').toLowerCase().includes(tripSearchQuery.toLowerCase()))
            .map(trip => ({
                id: trip.id,
                name: trip.name,
                description: trip.description,
                events: trip.events,
                issueCount: tripIssueCounts[trip.id] ?? 0,
            }));
    }, [trips, tripSearchQuery, tripIssueCounts]);

    const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
        navigate(newValue === TAB_TRIPS ? '/logistics/trips' : '/logistics/operations');
    };

    const handleTripClick = (tripId: string) => {
        navigate(`/logistics/trip/${tripId}`);
    };

    const handleTripDeleteClick = (e: React.MouseEvent, trip: { id: string; name?: string | null }) => {
        e.stopPropagation();
        setTripToDelete(trip);
        setDeleteTripError(null);
        setDeleteTripDialogOpen(true);
    };

    const handleTripDeleteConfirm = async () => {
        if (!tripToDelete) return;
        setDeleteTripError(null);
        try {
            await db.transact([db.tx.trips[tripToDelete.id].delete()]);
            setDeleteTripDialogOpen(false);
            setTripToDelete(null);
        } catch (e) {
            setDeleteTripError(e instanceof Error ? e.message : 'Failed to delete trip');
        }
    };

    if (!permissionsLoading && !canViewInventory) {
        return <PermissionDenied requiredPermission="View Inventory" />;
    }

    if (operationsLoading) {
        return (
            <DefaultLayout>
                <Box display="flex" justifyContent="center" padding={4}>
                    <CircularProgress />
                </Box>
            </DefaultLayout>
        );
    }

    return (
        <DefaultLayout>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flexGrow: 1,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    width: '100%',
                    minWidth: 0,
                    height: '100%',
                    boxSizing: 'border-box',
                }}
            >
                <GlobalHeader title="Logistics" />
                <Container maxWidth="lg" disableGutters sx={{ flexGrow: 1, py: 3, px: { xs: 2, sm: 3 }, minWidth: 0, maxWidth: '100%' }}>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3, minWidth: 0 }}>
                        <Tabs
                            value={activeTab}
                            onChange={handleTabChange}
                            aria-label="logistics tabs"
                            variant="scrollable"
                            scrollButtons="auto"
                            allowScrollButtonsMobile
                        >
                            <Tab label="Operations" id="logistics-tab-0" aria-controls="logistics-tabpanel-0" />
                            <Tab label="Trips" id="logistics-tab-1" aria-controls="logistics-tabpanel-1" />
                        </Tabs>
                    </Box>

                    {/* Operations tab */}
                    <TabPanel value={activeTab} index={TAB_OPERATIONS} idPrefix="logistics">
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {activeOperationsError && (
                                <Alert severity="error">Error loading active operations: {activeOperationsError.message}</Alert>
                            )}
                            {completedOperationsError && (
                                <Alert severity="error">Error loading completed operations: {completedOperationsError.message}</Alert>
                            )}
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                Packing operations created from this tab follow packing lists and current inventory: task quantities come
                                from target events&apos; packing lists and are allocated from inventory at your selected sources (events or
                                storage).
                            </Typography>
                            <OperationsListSection
                                activeOperations={activeOperations}
                                completedOperations={completedOperations}
                                operationProgress={op => getOperationProgress(op as PackingOperationList)}
                                formatSummary={op => formatOperationSummary(op as OperationSummaryInput)}
                                onOperationClick={op => navigate(`/logistics/operation/${op.id}/tasks`)}
                                onCreateOperation={() => setCreateOperationDialogOpen(true)}
                                expandedSections={expandedOperationsSections}
                                onAccordionChange={handleOperationsAccordionChange}
                                showTripLink
                            />
                        </Box>
                    </TabPanel>

                    {/* Trips tab */}
                    <TabPanel value={activeTab} index={TAB_TRIPS} idPrefix="logistics">
                        {tripsLoading ? (
                            <Stack spacing={2}>
                                <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
                                <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
                                {[1, 2, 3, 4].map(i => (
                                    <Skeleton key={i} variant="rectangular" height={80} sx={{ borderRadius: 1 }} />
                                ))}
                            </Stack>
                        ) : tripsError ? (
                            <Alert severity="error">Error loading trips: {tripsError.message}</Alert>
                        ) : (
                            <TripsListSection
                                filteredTrips={filteredTrips}
                                searchQuery={tripSearchQuery}
                                onSearchChange={setTripSearchQuery}
                                onCreateTrip={() => setCreateTripDialogOpen(true)}
                                onTripClick={handleTripClick}
                                onDeleteClick={handleTripDeleteClick}
                            />
                        )}
                    </TabPanel>
                </Container>
            </Box>

            <CreateOperationDialog open={createOperationDialogOpen} onClose={() => setCreateOperationDialogOpen(false)} />

            <CreateTripDialog
                open={createTripDialogOpen}
                onClose={() => setCreateTripDialogOpen(false)}
                onCreated={() => setCreateTripDialogOpen(false)}
            />

            <DeleteTripDialog
                open={deleteTripDialogOpen}
                trip={tripToDelete}
                error={deleteTripError}
                onConfirm={handleTripDeleteConfirm}
                onClose={() => setDeleteTripDialogOpen(false)}
            />
        </DefaultLayout>
    );
}
